import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api-client';
import { useAuth } from './AuthContext';
import { resolveTier } from '../hooks/useTier';

const SESSION_KEY = 'activeCompanyId';

interface CompanyInfo {
  id: string;
  name: string;
  code: string;
  status: string;
}

interface CompanyContextType {
  activeCompanyId: string | null;
  activeCompany: CompanyInfo | null;
  companies: CompanyInfo[];
  setActiveCompany: (id: string) => void;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | null>(null);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Only a superadmin spans companies and may switch. Standard/manager admins
  // are pinned to their own company (like sellers/sub-sellers).
  const isSuperAdmin = resolveTier(user) === 'super';

  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(() => {
    if (!isSuperAdmin && user?.companyId) return user.companyId;
    return sessionStorage.getItem(SESSION_KEY);
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => apiGet<{ items: CompanyInfo[] }>('/admin/companies'),
    enabled: isSuperAdmin,
  });

  const companies: CompanyInfo[] = isSuperAdmin && data?.ok ? data.data.items : [];

  const pinnedCompany: CompanyInfo | null =
    !isSuperAdmin && user?.companyId
      ? { id: user.companyId, name: '', code: '', status: 'active' }
      : null;

  const activeCompany =
    companies.find((c) => c.id === activeCompanyId) ?? (!isSuperAdmin ? pinnedCompany : null);

  const setActiveCompany = useCallback((id: string) => {
    setActiveCompanyIdState(id);
    sessionStorage.setItem(SESSION_KEY, id);
  }, []);

  useEffect(() => {
    if (isSuperAdmin && companies.length > 0 && !activeCompanyId) {
      const first = companies.find((c) => c.status === 'active') ?? companies[0];
      setActiveCompany(first.id);
    }
  }, [isSuperAdmin, companies, activeCompanyId, setActiveCompany]);

  return (
    <CompanyContext.Provider
      value={{ activeCompanyId, activeCompany, companies, setActiveCompany, isLoading }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyContextType {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within a CompanyProvider');
  return ctx;
}
